import { SettingItem, Currency, Receipt, SafeUser, BulkUpdatePayload, ProductMapping, ProductGroup, User, ColorSuggestion, MergeSuggestionGroup, AiModel, AiPrompts, CurrencyStat, DashboardOverviewData, PriceTrackerProductData, API_TIMEOUT_PREF_KEY } from '../types';
import { GetReceiptsOptions, UploadResult } from '../types';
import { logger } from './logger';
import { getApiUrl, dataURLtoBlob } from './utils';

// --- Fix: Added types for processing queue ---
export interface QueueItem {
    id: string;
    originalFilename: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    errorMessage: string | null;
    createdAt: string;
}

export interface QueueLog {
    id: number;
    timestamp: string;
    level: 'INFO' | 'DEBUG' | 'WARN' | 'ERROR';
    message: string;
}

export type ProcessorStatus = 'idle' | 'running' | 'stopping';

export interface QueueSummary {
    status: ProcessorStatus;
    counts: {
        total: number;
        pending: number;
        processing: number;
        completed: number;
        failed: number;
    };
    lastUpdate: string | null; // ISO string
}


// --- Token Refresh Logic ---
let isRefreshing = false;
let failedQueue: ((token: string) => void)[] = [];

const processQueue = (token: string) => {
    failedQueue.forEach(callback => callback(token));
    failedQueue = [];
};

const handleLogout = () => {
    window.dispatchEvent(new CustomEvent('auth-expired'));
    // Clear the queue to prevent hanging promises
    failedQueue = [];
    isRefreshing = false;
};

let apiTimeout = 60000; // Výchozí hodnota 60 sekund

export const configureApiService = async () => {
    try {
        const timeoutSeconds = await apiService.getPreference<number>(API_TIMEOUT_PREF_KEY, 60);
        if (timeoutSeconds > 0) {
            apiTimeout = timeoutSeconds * 1000;
            logger.info(`[API Service] Timeout nakonfigurován na ${timeoutSeconds}s.`);
        }
    } catch(e) {
        logger.error("[API Service] Nepodařilo se načíst konfiguraci timeoutu.", e);
    }
};

const getApiTimeout = (): number => apiTimeout;

const _refreshTokenInternal = async (): Promise<string> => {
    logger.info("[API] Access token vypršel, pokouším se o obnovení...");
    const refreshToken = sessionStorage.getItem('receiptApp.refreshToken');
    if (!refreshToken) {
        logger.error("[API] Refresh token nebyl nalezen, nelze obnovit přihlášení.");
        throw new Error("Refresh token nebyl nalezen.");
    }
    
    // Použijeme přímo fetch, abychom se vyhnuli rekurzi v apiFetch
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: refreshToken }),
    });

    if (!response.ok) {
        logger.error("[API] Nepodařilo se obnovit access token z BE.");
        throw new Error("Nepodařilo se obnovit access token.");
    }

    const data = await response.json();
    if (data.accessToken) {
        logger.info("[API] Access token úspěšně obnoven.");
        sessionStorage.setItem('receiptApp.accessToken', data.accessToken);
        return data.accessToken;
    }
    
    throw new Error("Odpověď z BE neobsahovala nový access token.");
};


// --- Core API Fetch Function ---
const apiFetch = async (endpoint: string, options: RequestInit = {}, requiresAuth: boolean = true): Promise<Response> => {
    const apiUrl = getApiUrl();
    const controller = new AbortController();
    const timeoutValue = getApiTimeout();
    
    const timeoutId = setTimeout(() => {
        logger.warn(`[API] Požadavek na ${endpoint} vypršel (${timeoutValue / 1000}s).`);
        controller.abort();
    }, timeoutValue);

    const headers = new Headers(options.headers);
    if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (requiresAuth) {
        const token = sessionStorage.getItem('receiptApp.accessToken');
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
    }

    const finalOptions = { ...options, headers, signal: controller.signal };

    try {
        logger.debug(`[API] ===> ${finalOptions.method || 'GET'} ${apiUrl}${endpoint}`, finalOptions.body ? { body: '...' } : {});
        const response = await fetch(`${apiUrl}${endpoint}`, finalOptions);
        clearTimeout(timeoutId);

        if (response.ok) {
            return response;
        }

        const errorData = await response.json().catch(() => ({ message: response.statusText, error: response.statusText }));
        logger.error(`[API] <=== ERROR ${response.status} from ${endpoint}`, { errorData });

        if (response.status === 401 && errorData.error === 'Platnost přístupového tokenu vypršela.') {
            if (isRefreshing) {
                return new Promise<Response>((resolve) => {
                    failedQueue.push((newToken) => {
                        const newHeaders = new Headers(finalOptions.headers);
                        newHeaders.set('Authorization', `Bearer ${newToken}`);
                        resolve(apiFetch(endpoint, { ...finalOptions, headers: newHeaders }, requiresAuth));
                    });
                });
            }

            isRefreshing = true;
            try {
                const newAccessToken = await _refreshTokenInternal();
                processQueue(newAccessToken);
                const newHeaders = new Headers(finalOptions.headers);
                newHeaders.set('Authorization', `Bearer ${newAccessToken}`);
                return apiFetch(endpoint, { ...finalOptions, headers: newHeaders }, requiresAuth);
            } catch (refreshError) {
                handleLogout();
                throw refreshError;
            } finally {
                isRefreshing = false;
            }
        }
        
        if (response.status === 401 || response.status === 403) {
            handleLogout();
        }

        throw new Error(errorData.error || errorData.details || errorData.message || `API požadavek selhal se statusem ${response.status}`);
    } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Požadavek na server vypršel (${timeoutValue / 1000}s). Můžete zkusit navýšit časový limit v nastavení aplikace.`);
        }
        logger.error(`[API] <=== NETWORK/FETCH ERROR on ${endpoint}`, error);
        throw error;
    }
};

const apiRequest = async <T>(endpoint: string, options: RequestInit = {}, requiresAuth: boolean = true): Promise<T> => {
    const response = await apiFetch(endpoint, options, requiresAuth);

    if (response.status === 204 || (response.headers.get('content-length') === '0')) {
        logger.info(`[API] <=== SUCCESS 204 No Content from ${endpoint}`);
        return {} as T;
    }

    const responseData = await response.json();
    logger.info(`[API] <=== SUCCESS ${response.status} from ${endpoint}`);
    logger.debug(`[API] Response data from ${endpoint}:`, responseData);
    return responseData;
};

const buildFilterParams = (options: GetReceiptsOptions): URLSearchParams => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', String(options.page));
    if (options.limit !== undefined && options.limit !== null) params.append('limit', String(options.limit));
    if (options.searchTerm) params.append('searchTerm', options.searchTerm);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.sortConfig) {
        params.append('sortKey', options.sortConfig.key);
        params.append('sortDirection', options.sortConfig.direction);
    }
    
    options.tags_include?.forEach(t => params.append('tags_include', t));
    options.tags_exclude?.forEach(t => params.append('tags_exclude', t));
    options.businessNames_include?.forEach(b => params.append('businessNames_include', b));
    options.businessNames_exclude?.forEach(b => params.append('businessNames_exclude', b));
    options.categories_include?.forEach(c => params.append('categories_include', c));
    options.categories_exclude?.forEach(c => params.append('categories_exclude', c));
    options.paymentMethods_include?.forEach(p => params.append('paymentMethods_include', p));
    options.paymentMethods_exclude?.forEach(p => params.append('paymentMethods_exclude', p));
    options.currencies_include?.forEach(c => params.append('currencies_include', c));
    options.currencies_exclude?.forEach(c => params.append('currencies_exclude', c));

    return params;
}


export const apiService = {
    login: async (username, password): Promise<{ user: SafeUser, accessToken: string, refreshToken: string } | null> => {
        const response = await apiRequest<{ accessToken: string, refreshToken: string, user: SafeUser }>('/api/login', {
            method: 'POST',
            body: JSON.stringify({ username, password })
        }, false);

        if (response && response.accessToken && response.user) {
            return response;
        }
        return null;
    },

    // --- Generic CRUD ---
    getAll: <T>(storeName: string): Promise<T[]> => apiRequest<T[]>(getEndpointForStore(storeName)),
    
    saveAll: async <T extends { id?: number }>(storeName: string, items: T[]): Promise<void> => {
        if (items.length === 0) return;
        const endpoint = getEndpointForStore(storeName);
        const updatePromises = items.map(item => {
            if (!item.id) return Promise.resolve();
            const { id, ...data } = item;
            return apiRequest(`${endpoint}/${id}`, { method: 'PUT', body: JSON.stringify(data) });
        });
        await Promise.all(updatePromises);
    },

    deleteItem: (storeName: string, id: number | string) => apiRequest(`${getEndpointForStore(storeName)}/${id}`, { method: 'DELETE' }),

    findOrCreate: (storeName: string, itemData: any): Promise<SettingItem | Currency> => apiRequest<SettingItem | Currency>(getEndpointForStore(storeName), {
        method: 'POST',
        body: JSON.stringify(itemData)
    }),

    // --- Users ---
    getAllUsers: () => apiRequest<SafeUser[]>('/api/users'),
    addUser: (username, password) => apiRequest<SafeUser>('/api/users', {
        method: 'POST',
        body: JSON.stringify({ username, password })
    }),
    updateUser: (userId, updates) => apiRequest(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
    }),
    deleteUser: (userId) => apiRequest(`/api/users/${userId}`, { method: 'DELETE' }),

    addBusiness: (name: string): Promise<{ id: number, name: string }> => apiRequest('/api/businesses', {
        method: 'POST',
        body: JSON.stringify({ name })
    }),

    // --- Receipts ---
    getReceipts: (options: GetReceiptsOptions): Promise<{ receipts: Receipt[]; total: number; }> => {
        const params = buildFilterParams(options);
        
        return apiRequest<{ receipts: any[]; total: number }>(`/api/receipts?${params.toString()}`)
            .then(response => {
                const parsedReceipts: Receipt[] = response.receipts.map(r => ({
                    ...r,
                    // The backend sends DECIMAL as a string for precision, parse it to number here
                    amount: (r.amount === null || r.amount === undefined) ? r.amount : Number(r.amount)
                }));
                return { receipts: parsedReceipts, total: response.total };
            });
    },
    
    getReceiptsSummary: (options: Omit<GetReceiptsOptions, 'page' | 'limit' | 'sortConfig'>): Promise<{ currency: string, total: number }[]> => {
        const params = buildFilterParams(options);
        return apiRequest(`/api/receipts/summary?${params.toString()}`);
    },

    getFilteredReceiptIds: (options: Omit<GetReceiptsOptions, 'page' | 'limit' | 'sortConfig'>): Promise<{ ids: string[] }> => {
        const params = buildFilterParams(options);
        return apiRequest<{ ids: string[] }>(`/api/receipts/ids?${params.toString()}`);
    },

    updateReceipt: (receipt) => apiRequest(`/api/receipts/${receipt.id}`, {
        method: 'PUT',
        body: JSON.stringify(receipt)
    }),
    deleteReceipt: (id) => apiRequest(`/api/receipts/${id}`, { method: 'DELETE' }),
    bulkDeleteReceipts: (ids) => apiRequest('/api/receipts/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids })
    }),
    bulkUpdateReceipts: (ids, updates) => apiRequest('/api/receipts/bulk-update', {
        method: 'POST',
        body: JSON.stringify({ ids, updates })
    }),
    
    uploadFilesToQueue: (files: File[], tags: SettingItem[]): Promise<{ message: string; items: QueueItem[] }> => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        formData.append('tags', JSON.stringify(tags));
        return apiRequest('/api/queue/upload', {
            method: 'POST',
            body: formData,
        });
    },

    importCsv: (formData: FormData) => apiRequest<{ message: string }>('/api/receipts/import-csv', {
        method: 'POST',
        body: formData,
    }),

    triggerCsvExport: async (filters: Omit<GetReceiptsOptions, 'page' | 'limit' | 'sortConfig'>, columns: string[]) => {
        const params = buildFilterParams(filters);
        columns.forEach(c => params.append('columns', c));

        const endpoint = `/api/receipts/export-csv?${params.toString()}`;
        const blob = await apiService.downloadFileBlob(endpoint);

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export_uctenek_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },

    // --- Preferences ---
    getPreference: <T>(key: string, defaultValue: T): Promise<T> => 
        apiRequest<{value: T}>(`/api/preferences/${key}`).then(res => res.value ?? defaultValue),

    setPreference: <T>(key: string, value: T): Promise<void> => 
        apiRequest(`/api/preferences/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
    
    // --- Unique values & Aggregations ---
    getUniqueBusinessNames: () => apiRequest<{ name: string; count: number }[]>('/api/unique/business-names'),
    getUniqueReceiptYears: () => apiRequest<number[]>('/api/unique/years'),
    getYearlyMonthlyExpenses: (years: number[], currency: string, dateFrom: string | null, dateTo: string | null) => {
        const params = new URLSearchParams();
        params.append('years', years.join(','));
        params.append('currency', currency);
        if (dateFrom) params.set('dateFrom', dateFrom);
        if (dateTo) params.set('dateTo', dateTo);
        return apiRequest<Record<string, { expenses: number; refunds: number; }>>(`/api/unique/monthly-expenses?${params.toString()}`);
    },

    // --- Dashboard ---
    getDashboardStats: (filters: Omit<GetReceiptsOptions, 'page' | 'limit' | 'sortConfig'>): Promise<CurrencyStat[]> => {
        const params = buildFilterParams(filters);
        return apiRequest(`/api/dashboard/stats?${params.toString()}`);
    },
    getDashboardOverview: (filters: Omit<GetReceiptsOptions, 'page' | 'limit' | 'sortConfig' | 'currencies_include' | 'currencies_exclude'>, currency: string): Promise<DashboardOverviewData> => {
        const params = buildFilterParams(filters);
        params.set('currency', currency); // This specific endpoint takes a single currency
        return apiRequest(`/api/dashboard/overview?${params.toString()}`);
    },

    // --- Price Tracker ---
    getPriceTrackerUniqueProducts: (dateFrom: string | null, dateTo: string | null): Promise<string[]> => {
        const params = new URLSearchParams();
        if (dateFrom) params.set('dateFrom', dateFrom);
        if (dateTo) params.set('dateTo', dateTo);
        // Assuming a new endpoint exists for this
        return apiRequest(`/api/pricetracker/unique-products?${params.toString()}`);
    },
    getPriceTrackerProductData: (dateFrom: string | null, dateTo: string | null, productName: string): Promise<PriceTrackerProductData> => {
        const params = new URLSearchParams();
        if (dateFrom) params.set('dateFrom', dateFrom);
        if (dateTo) params.set('dateTo', dateTo);
        params.set('productName', productName);
        // Assuming a new endpoint exists for this
        return apiRequest(`/api/pricetracker/product-data?${params.toString()}`);
    },


    // --- Product Normalization ---
    groupSimilarProducts: (names: string[]): Promise<ProductGroup[]> => apiRequest('/api/products/suggest-groups', {
        method: 'POST',
        body: JSON.stringify({ names }),
    }),
    getAllUniqueProductNames: () => apiRequest<string[]>('/api/products/unique-names'),
    normalizeProductNames: (groups: ProductGroup[]) => apiRequest<{ updatedCount: number }>('/api/products/normalize', {
        method: 'POST',
        body: JSON.stringify(groups),
    }),
    saveProductMappings: (groups: ProductGroup[]) => apiRequest(`/api/products/mappings`, {
        method: 'POST',
        body: JSON.stringify(groups)
    }),
    deleteProductMapping: (originalName: string) => apiRequest(`/api/products/mappings/${encodeURIComponent(originalName)}`, {
        method: 'DELETE',
    }),

    // --- Advanced Ops & AI ---
    mergeSettingItems: (storeName, sourceId, targetId) => apiRequest(`/api/settings/${storeName}/merge`, {
        method: 'POST',
        body: JSON.stringify({ sourceId, targetId })
    }),
    updateBusinessName: (oldName, newName) => apiRequest('/api/business-names/rename', {
        method: 'PUT',
        body: JSON.stringify({ oldName, newName })
    }),
    mergeBusiness: (sourceName: string, targetName: string): Promise<{ message: string }> => apiRequest('/api/businesses/merge', {
        method: 'POST',
        body: JSON.stringify({ sourceName, targetName })
    }),
    suggestColorsForItems: (items: string[]): Promise<ColorSuggestion[]> => apiRequest('/api/settings/suggest-colors', {
        method: 'POST',
        body: JSON.stringify({ items }),
    }),

    suggestSimilarItemsToMerge: (items: string[]): Promise<MergeSuggestionGroup[]> => apiRequest('/api/settings/suggest-merges', {
        method: 'POST',
        body: JSON.stringify({ items }),
    }),

    getBackendVersion: (): Promise<{ version: string; commit: string }> => apiRequest('/api/version', {}, false),

    testUpload: (formData: FormData): Promise<{ message: string, url: string }> => apiRequest('/api/test/upload', {
        method: 'POST',
        body: formData,
    }),

    downloadFileBlob: async (relativeUrl: string): Promise<Blob> => {
        const response = await apiFetch(relativeUrl, {}, true);
        return response.blob();
    },

    healthCheck: async (url: string): Promise<{ status: string; message: string }> => {
        const cleanUrl = url.trim().replace(/\/$/, '');
        const healthUrl = `${cleanUrl}/health`;
        logger.info(`[API Test] Spouštím test připojení na adresu: ${healthUrl}`);
        try {
            const response = await fetch(healthUrl);
            if (!response.ok) {
                const errorText = await response.text().catch(() => `HTTP status ${response.status}`);
                logger.error(`[API Test] Selhalo s HTTP statusem ${response.status}. Odpověď: ${errorText}`);
                throw new Error(`Test spojení selhal: ${errorText}`);
            }
            const result = await response.json();
            logger.info('[API Test] Spojení úspěšné.', result);
            return result;
        } catch (e: any) {
            logger.error(`[API Test] Selhalo s chybou sítě:`, e.message);
            throw new Error(`Test spojení selhal: ${e.message}. Zkontrolujte, zda je adresa správná, server běží a nejsou zde problémy s CORS.`);
        }
    },
    
    getAvailableAiModels: (): Promise<AiModel[]> => apiRequest('/api/ai/models'),

    getAiPrompts: (): Promise<AiPrompts> => apiRequest('/api/ai/prompts'),
    updateAiPrompts: (prompts: AiPrompts): Promise<void> => apiRequest('/api/ai/prompts', {
        method: 'PUT',
        body: JSON.stringify(prompts),
    }),
    getDefaultAiPrompts: (): Promise<AiPrompts> => apiRequest('/api/ai/prompts/default'),
    
    // --- Fix: Added new methods for processing queue ---
    getQueue: (): Promise<QueueItem[]> => apiRequest('/api/queue'),
    getQueueLogs: (page: number): Promise<{ logs: QueueLog[]; totalPages: number }> => apiRequest(`/api/queue/logs?page=${page}`),
    getQueueStatus: (): Promise<{ status: ProcessorStatus }> => apiRequest('/api/queue/status'),
    getQueueSummary: (): Promise<QueueSummary> => apiRequest('/api/queue/summary'),
    startQueue: (): Promise<{ message: string }> => apiRequest('/api/queue/start', { method: 'POST' }),
    stopQueue: (): Promise<{ message: string }> => apiRequest('/api/queue/stop', { method: 'POST' }),
    clearCompletedInQueue: (): Promise<void> => apiRequest('/api/queue/clear-completed', { method: 'DELETE' }),
    deleteQueueItem: (id: string): Promise<void> => apiRequest(`/api/queue/${id}`, { method: 'DELETE' }),

    // PaperSnap Integration
    scanDocument: async (
        imageSrc: string, // data URL
        effect: string,
        corners: { x: number; y: number }[] | null
    ): Promise<Blob> => {
        const imageBlob = dataURLtoBlob(imageSrc);

        const formData = new FormData();
        formData.append('file', imageBlob, 'scan.jpg');
        formData.append('effect', effect);
        if (corners) {
            // PaperSnap expects [[x1,y1], [x2,y2], ...]
            formData.append('corners', JSON.stringify(corners.map(c => [c.x, c.y])));
        }

        const response = await apiFetch('/papersnap/api/v1/scan', {
            method: 'POST',
            body: formData,
        }, false); // No auth needed for papersnap

        return response.blob();
    },
};

const getEndpointForStore = (storeName: string): string => {
    const map: { [key: string]: string } = {
        'categories': '/api/categories', 'tags': '/api/tags', 'paymentMethods': '/api/payment-methods',
        'currencies': '/api/currencies', 'users': '/api/users', 'productMappings': '/api/products/mappings',
    };
    if (!map[storeName]) throw new Error(`Endpoint for store '${storeName}' is not defined.`);
    return map[storeName];
};